import { useState, useEffect } from 'react';
import * as api from '../api/api';

/**
 * Hook para gestionar la carga de supervisores por rango de fechas
 * @param {Object} dateRange - Rango de fechas con startDate y endDate
 * @param {Function} onSupervisorsLoaded - Callback cuando se cargan supervisores (para limpiar selecciones)
 * @returns {Object} { supervisors, supervisorsEnabled, isLoading, error }
 */
export function useSupervisors(dateRange, onSupervisorsLoaded) {
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorsEnabled, setSupervisorsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let abortController = new AbortController();
    
    const fetchSupervisors = async () => {
      // Si no hay rango de fechas, limpiar y deshabilitar
      if (!dateRange?.startDate || !dateRange?.endDate) {
        setSupervisorsEnabled(false);
        setSupervisors([]);
        setIsLoading(false);
        setError(null);
        if (onSupervisorsLoaded) {
          onSupervisorsLoaded(null, []);
        }
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        setSupervisorsEnabled(false);
        
        // Cargar solo supervisores
        const supervisorsRes = await api.getSupervisorsByDateRange(
          dateRange.startDate, 
          dateRange.endDate
        );

        // Verificar si se canceló el request (race condition)
        if (abortController.signal.aborted) return;

        // Dedupe defensivo frontend (por si backend devuelve duplicados por error)
        const uniqueSupervisors = [...new Set(supervisorsRes.items || [])].filter(s => s?.trim());

        // Ordenar alfabéticamente y convertir a formato de opciones para Select (Cloudscape)
        const formattedSupervisors = uniqueSupervisors.sort().map(s => ({label: s, value: s}));
        setSupervisors(formattedSupervisors);
        
        // Habilitar dropdown de supervisores
        setSupervisorsEnabled(true);
        
        // Notificar que se cargaron supervisores (para limpiar selecciones)
        if (onSupervisorsLoaded) {
          onSupervisorsLoaded(null, []);
        }
        
        console.log(`Supervisores cargados: ${uniqueSupervisors.length}`);
      } catch (err) {
        console.error('Error fetching supervisors by date range:', err);
        // En caso de error, mantener combo deshabilitado
        setSupervisorsEnabled(false);
        setSupervisors([]);
        setError(err);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchSupervisors();
    
    // Cleanup: cancelar requests si cambia el rango antes de que termine
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]); // onSupervisorsLoaded es estable (useCallback sin deps)

  return {
    supervisors,
    supervisorsEnabled,
    isLoading,
    error
  };
}

